// Key custody, client side.
//
// A5 owns the real thing: a passkey with the PRF extension, plus a printed
// recovery code, each wrapping the *same* random account key. Until that
// lands this implements the recovery-code half honestly — real entropy, real
// HKDF, real AES-GCM wrapping — and keeps the code in localStorage so the app
// is usable. That is the one shortcut, and it is marked here rather than
// hidden.

import * as F from './format';

const STORE = 'wa.recovery';
const IDENTITY = 'wa.identity';

/**
 * A per-browser identity for the development sign-in.
 *
 * The dev auth path trusts whatever subject it is given, so a fixed one made
 * every browser the same account: two people signing in shared an archive and
 * neither could decrypt the other's chats. A5's passkeys replace this
 * entirely; until then a browser is its own user.
 */
export function devIdentity(): string {
  let id = localStorage.getItem(IDENTITY);
  if (!id) {
    id = F.toHex(crypto.getRandomValues(new Uint8Array(16)));
    localStorage.setItem(IDENTITY, id);
  }
  return `dev-${id}@local`;
}

export interface Credential {
  /** 'escrow' is the service holding the key; this path cannot unwrap one and
   *  ignores it. See D-011. */
  method: 'passkey' | 'recovery' | 'escrow';
  version: number;
  credentialSalt: string;
  wrappedKey: string;
}

const b64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export function recoveryCode(): { entropy: Uint8Array; words: string } {
  const stored = localStorage.getItem(STORE);
  const entropy = stored ? F.fromHex(stored) : crypto.getRandomValues(new Uint8Array(32));
  if (!stored) localStorage.setItem(STORE, F.toHex(entropy));
  const hex = F.toHex(entropy);
  return { entropy, words: (hex.match(/.{8}/g) ?? []).join(' ') };
}

/** First sign-in only: a random account key, wrapped by the recovery
 *  credential. Calling this on an account that already has one would strand
 *  everything already imported. */
export async function createAccountKey() {
  const accountKey = crypto.getRandomValues(new Uint8Array(32));
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const { entropy } = recoveryCode();
  const kek = await F.kek(entropy, salt, 'recovery');
  const wrapped = await F.wrapAccountKey(accountKey, kek, crypto.getRandomValues(new Uint8Array(12)));
  const credential: Credential = {
    method: 'recovery', version: 1,
    credentialSalt: b64(salt), wrappedKey: b64(wrapped),
  };
  localStorage.setItem('wa.key', F.toHex(accountKey));
  return { accountKey, credential };
}

/**
 * Unwrap the server-held blob with this device's recovery code.
 *
 * The cache is checked against the server's wrapped key rather than trusted:
 * a stale cached key decrypts nothing and produces an archive full of
 * unreadable chats, which is far worse than one extra unwrap.
 */
export async function unlock(credentials: Credential[]): Promise<Uint8Array> {
  const cred = credentials.find((c) => c.method === 'recovery');
  if (!cred) throw new Error('This account has no recovery credential.');

  const { entropy } = recoveryCode();
  const kek = await F.kek(entropy, unb64(cred.credentialSalt), 'recovery');

  let accountKey: Uint8Array;
  try {
    accountKey = await F.unwrapAccountKey(unb64(cred.wrappedKey), kek);
  } catch {
    throw new Error(
      'The recovery code in this browser does not match this account. '
      + 'Enter the recovery code you were shown when the account was created.',
    );
  }

  const cached = localStorage.getItem('wa.key');
  if (cached && cached !== F.toHex(accountKey))
    console.warn('[keys] cached account key was stale and has been replaced');
  localStorage.setItem('wa.key', F.toHex(accountKey));
  return accountKey;
}

export const forget = () => { localStorage.removeItem('wa.key'); };
